/**
 * KAN-746: таблица заказов: номер (ссылка на заказ), клиент (ссылка на
 * карточку), менеджер, позиций, сумма, дата обновления, метка "брошена" у
 * брошенной корзины. В карточке клиента колонка клиента скрыта.
 * Широкая таблица прокручивается внутри своей рамки.
 */
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatRub } from '@/lib/money';
import type { OrderListItem } from '@/types/order';
import { formatOrderDate, orderNumber, orderStatusView, toAmount } from './orderFormat';

interface OrdersTableProps {
  orders: OrderListItem[];
  /** Колонка клиента (в карточке клиента не нужна). */
  showClient?: boolean;
  /** Колонка статуса (в списке по вкладке статус и так ясен). */
  showStatus?: boolean;
}

export function OrdersTable({ orders, showClient = true, showStatus = false }: OrdersTableProps) {
  return (
    <div className="overflow-x-auto">
      <Table aria-label="Заказы" data-testid="orders-table">
        <TableHeader>
          <TableRow>
            <TableHead>Номер</TableHead>
            {showStatus && <TableHead>Статус</TableHead>}
            {showClient && <TableHead>Клиент</TableHead>}
            <TableHead>Менеджер</TableHead>
            <TableHead className="text-right">Позиций</TableHead>
            <TableHead className="text-right">Сумма</TableHead>
            <TableHead>Обновлён</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => {
            const status = orderStatusView(order.status);
            return (
              <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                <TableCell className="whitespace-nowrap">
                  <Link to={`/orders/${order.id}`} className="font-medium text-blue-600 hover:underline">
                    {orderNumber(order.short_id)}
                  </Link>
                  {order.abandoned && (
                    <Badge variant="pending" className="ml-2" data-testid={`order-abandoned-${order.id}`}>
                      брошена
                    </Badge>
                  )}
                </TableCell>
                {showStatus && (
                  <TableCell>
                    <Badge variant={status.variant} className="whitespace-nowrap">
                      {status.label}
                    </Badge>
                  </TableCell>
                )}
                {showClient && (
                  <TableCell className="min-w-[160px]">
                    {order.client ? (
                      <Link to={`/clients/${order.client.id}`} className="text-blue-600 hover:underline">
                        {order.client.name || 'Без имени'}
                      </Link>
                    ) : (
                      '-'
                    )}
                    {order.client?.company && (
                      <span className="block text-xs text-gray-500">{order.client.company}</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-sm text-gray-700">{order.manager?.full_name || '-'}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{order.items_count}</TableCell>
                <TableCell className="whitespace-nowrap text-right text-sm font-medium">
                  {formatRub(toAmount(order.total))}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm text-gray-500">
                  {formatOrderDate(order.updated_at)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
